import { useQuery } from '@tanstack/react-query';
import { diaryManagementService } from '../../services/diaryManagementService';
import type {
  OrganisedMeetingsFocusResponse,
  AccessibleCalendarsResponse,
} from '../../types/organisedMeetings';

interface UseDataResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

export const diaryManagementQueryKeys = {
  all: ['diaryManagement'] as const,
  accessibleCalendars: () => [...diaryManagementQueryKeys.all, 'accessibleCalendars'] as const,
  organisedMeetingsFocus: (startDate: string, endDate: string) => 
    [...diaryManagementQueryKeys.all, 'organisedMeetingsFocus', startDate, endDate] as const,
};

export const useOrganisedMeetingsFocus = (
  startDate: string,
  endDate: string
): UseDataResult<OrganisedMeetingsFocusResponse> => {
  const query = useQuery({
    queryKey: diaryManagementQueryKeys.organisedMeetingsFocus(startDate, endDate),
    queryFn: () => diaryManagementService.getOrganisedMeetingsFocus(startDate, endDate),
    staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
};

export const useAccessibleCalendars = (enabled = true): UseDataResult<AccessibleCalendarsResponse> => {
  const query = useQuery({
    queryKey: diaryManagementQueryKeys.accessibleCalendars(),
    queryFn: () => diaryManagementService.getAccessibleCalendars(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: false,
    enabled,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
};
